'use client';
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { FileText, Shield, RefreshCw, ChevronRight } from 'lucide-react';

type PolicyTab = 'terms' | 'privacy' | 'refund';

const TABS: { id: PolicyTab; label: string; icon: any; shortLabel: string }[] = [
  { id: 'terms', label: 'Terms and Conditions', shortLabel: 'Terms', icon: FileText },
  { id: 'privacy', label: 'Privacy Policy', shortLabel: 'Privacy', icon: Shield },
  { id: 'refund', label: 'Return & Refund Policy', shortLabel: 'Refund', icon: RefreshCw },
];

const TERMS_CONTENT = `TERMS AND CONDITIONS
SpeedPass Platform
Effective Date: 2026

1. ACCEPTANCE OF TERMS
By accessing or using the SpeedPass Platform ("Platform"), you agree to be bound by these Terms and Conditions. If you do not agree, please do not use the Platform.

2. ELIGIBILITY
Users must be at least 18 years old or have parental/guardian permission to register an account. By registering, you confirm that you meet this requirement and that all information provided is accurate and up-to-date.

3. PLATFORM SERVICES
The Platform offers the following services:
• Event information, registration, and scheduling
• Race results and live standings
• Media galleries and promotional campaigns
• Communication services between participants and organizers
• Track management and booking services

The Platform reserves the right to modify, suspend, or discontinue any service without prior notice.

4. USER ACCOUNT RESPONSIBILITIES
You are responsible for:
• Providing truthful and accurate information at all times
• Keeping your contact details updated
• Maintaining the confidentiality of your account credentials
• Avoiding the creation of fraudulent or multiple accounts
• Reporting any unauthorized access to your account immediately

Accounts found to be in violation of these responsibilities may be suspended or permanently terminated.

5. EVENT REGISTRATION
• Event registration may have eligibility requirements set by the organizer
• All fees must be paid before registration deadlines
• Event registration is generally non-transferable unless explicitly permitted by the organizer
• Event organizers reserve the right to refuse or cancel registrations for legitimate reasons

6. PAYMENTS
• Payments must be made using approved methods as specified during checkout
• Refunds and cancellations are subject to event-specific policies
• SpeedPass is not responsible for payment processing errors caused by third-party providers

7. USER CONDUCT
You must not:
• Submit false or misleading information
• Share unlawful, harmful, or offensive content
• Attempt unauthorized access to any part of the Platform
• Interfere with the Platform's operation or other users' experience
• Use the Platform for any illegal activities

Violation of these conduct rules may result in immediate suspension or termination of your account.

8. DATA AND PRIVACY
By using the Platform, you consent to the collection and processing of your personal data for:
• Account management and event registration
• Communication and support
• Promotional activities and service improvement
• Security and fraud prevention

Please refer to our Privacy Policy for full details on how your data is handled.

9. MEDIA AND INTELLECTUAL PROPERTY
• Event photos and media may be taken and used for promotional or archival purposes without additional compensation, unless prohibited by law
• All Platform content is owned by or licensed to SpeedPass and is protected by intellectual property laws
• Unauthorized use, reproduction, or distribution of Platform content is strictly prohibited

10. PROMOTIONS
SpeedPass may offer promotions with additional terms. In the event of a conflict, the promotion-specific terms shall prevail.

11. PLATFORM AVAILABILITY
Platform availability is not guaranteed. Maintenance, technical issues, or unforeseen circumstances may cause temporary downtime. SpeedPass will endeavor to minimize disruptions.

12. LIMITATION OF LIABILITY
SpeedPass is not liable for:
• Indirect, incidental, or consequential losses
• Event changes, cancellations, or postponements by organizers
• Technical failures or data loss
• Risks associated with participation in motorsport events

Participation in any event is entirely at the user's own risk.

13. ACCOUNT SUSPENSION AND TERMINATION
Accounts may be suspended or terminated for:
• Violations of these Terms and Conditions
• Fraudulent activity or misuse of the Platform
• Security risks or suspicious behavior
• Any other reason deemed appropriate by SpeedPass administration

14. AMENDMENTS TO TERMS
These Terms and Conditions may be updated from time to time. Continued use of the Platform following any changes constitutes acceptance of the updated terms. Users are encouraged to review this page periodically.

15. GOVERNING LAW
These Terms and Conditions are governed by the laws of Malaysia. Any disputes shall be resolved in accordance with applicable Malaysian law.

16. CONTACT US
For questions or concerns regarding these Terms and Conditions, please contact SpeedPass via the contact details available on our website at speedpass.live.`;

const PRIVACY_CONTENT = `PRIVACY POLICY
SpeedPass Platform
Effective Date: 7 August 2026

1. INTRODUCTION
SpeedPass ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our website and services.

2. INFORMATION WE COLLECT
We collect the following types of information:

Personal Information:
• Full name (as per IC), email address, phone number
• Date of birth, gender, and identification details
• Emergency contact information

Account Information:
• Username and password (encrypted)
• Login activity and session data

Event Information:
• Event registrations and payment status
• Attendance records and competition category
• Team information and race results

Media Information:
• Photographs, videos, and live stream footage taken during events
• Interviews and promotional content

Technical Information:
• IP address, browser type, and device information
• Operating system and website usage statistics
• Cookies and tracking data

3. HOW WE USE YOUR INFORMATION
Your information is used to:
• Manage your account and verify your identity
• Process event registrations and payments
• Verify eligibility for events and categories
• Communicate updates, confirmations, and support
• Publish event data and results
• Send marketing communications (with your consent)
• Maintain platform security and prevent fraud
• Comply with legal obligations

4. PHOTOS AND MEDIA
Photos and videos taken during events may be used for:
• Promotional materials and social media
• Marketing campaigns and news coverage
• Advertisements and sponsor promotions

This usage does not entitle participants to additional compensation unless a separate agreement has been made.

5. INFORMATION SHARING
We share your information only when necessary:
• With event organizers to manage registrations
• With payment providers to process transactions
• With technology and hosting providers for platform operation
• With sponsors when required for event participation
• With government authorities as required by law

We do not sell your personal information to third parties.

6. DATA SECURITY
We implement reasonable security measures to protect your data. However, no system is 100% secure. In the event of a data breach, we will notify affected users as required by law.

7. DATA RETENTION
We retain your data for as long as necessary for:
• Active account maintenance
• Event management and historical records
• Legal obligations and dispute resolution
• Archival and compliance purposes

8. COOKIES
We use cookies to:
• Remember your preferences and login sessions
• Improve platform performance and analytics
• Enhance your overall experience

You may disable cookies in your browser settings, but some features may not function properly.

9. YOUR RIGHTS
You have the right to:
• Access and review your personal data
• Correct inaccurate information
• Request deletion of your data (subject to legal requirements)
• Withdraw consent for marketing communications
• Object to certain types of data processing
• Request a copy of your data

To exercise these rights, contact us via the details on our website.

10. THIRD-PARTY SERVICES
The Platform may integrate with third-party services (e.g., payment gateways, analytics). SpeedPass is not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.

11. CHILDREN'S PRIVACY
Our services are not intended for children under the legal age of majority without parental consent. If we become aware that we have collected data from a minor without consent, we will take steps to remove that information.

12. UPDATES TO THIS POLICY
This Privacy Policy may be updated periodically. Continued use of the Platform following any changes constitutes acceptance of the updated policy. The effective date at the top of this page will reflect the most recent update.

13. CONTACT US
For privacy-related inquiries, please contact SpeedPass Ventures via the contact details available on our website at speedpass.live.`;

const REFUND_CONTENT = `RETURN AND REFUND POLICY
SpeedPass Platform
Effective Date: 12 August 2026

1. OVERVIEW
This Refund Policy governs all online event registrations and payment transactions made through the SpeedPass Platform. By completing a payment, you agree to the terms outlined in this policy.

2. GENERAL NON-REFUNDABLE POLICY
All event registrations are non-refundable once payment has been completed. This includes, but is not limited to:
• Change of mind after registration
• Failure to attend the event
• Late arrival or inability to participate
• Personal commitments or scheduling conflicts
• Illness or medical conditions (unless covered by separate insurance)
• Failure to meet eligibility requirements
• Incorrect registration information submitted by the participant

3. EVENT CANCELLATION BY ORGANIZER
If an event is officially cancelled by the organizer:
• Registered participants may be eligible for a full refund
• Refunds will be processed through the original payment method
• Processing time depends on the payment provider and is not controlled by SpeedPass

4. EVENT POSTPONEMENT
If an event is postponed:
• Your registration may be transferred to the new event date
• If you are unable to attend the new date, a refund request may be submitted within the period specified by the organizer
• Refund eligibility for postponed events is subject to the organizer's discretion

5. OUTDOOR EVENTS AND WEATHER CONDITIONS
For outdoor events:
• Weather conditions, unforeseen circumstances, or force majeure events may cause delays or partial cancellations
• No refund will be issued if a participant chooses not to attend due to weather unless the event is officially cancelled
• If the event is officially cancelled due to weather, the standard cancellation refund policy applies

6. DUPLICATE PAYMENTS
If a duplicate payment is verified by SpeedPass:
• A refund for the duplicate charge may be issued upon request
• Please contact us immediately with proof of the duplicate transaction

7. FAILED PAYMENT TRANSACTIONS
If a payment transaction fails but a deduction appears on your account:
• Allow 3–5 business days for the transaction to automatically reverse
• If the amount is not reversed within this period, contact SpeedPass with your transaction reference for investigation

8. REFUND PROCESSING
Approved refunds are:
• Processed through the original payment method used during registration
• Subject to processing times determined by your payment provider (typically 5–14 business days)
• Not controlled by SpeedPass once initiated with the payment provider

9. HOW TO REQUEST A REFUND
To submit a refund request, please provide the following information:
• Full name (as per IC)
• Registration or order number
• Email address used for registration
• Event name and date
• Payment date and transaction reference number
• Reason for refund request

Refund requests should be submitted promptly. Late requests may not be considered.

10. INDIVIDUAL EVENT POLICIES
Some events may have additional refund or cancellation terms displayed during the registration process. These event-specific terms take precedence over this general policy and are subject to Malaysian consumer protection law.

11. CONSUMER RIGHTS
This policy does not remove or restrict your legal rights as a consumer under Malaysian law. SpeedPass complies with all applicable consumer protection legislation.

12. CONTACT US
For refund enquiries, please contact SpeedPass via the contact details available on our website at speedpass.live. Our team will respond within 3–5 business days.`;

const CONTENT_MAP: Record<PolicyTab, string> = {
  terms: TERMS_CONTENT,
  privacy: PRIVACY_CONTENT,
  refund: REFUND_CONTENT,
};

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<PolicyTab>('terms');

  const content = CONTENT_MAP[activeTab];
  const lines = content.split('\n');

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <div className="border-b border-border bg-card/50">
          <div className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">SpeedPass</p>
            <h1 className="text-4xl font-extrabold uppercase tracking-tight text-foreground mb-2">About Us</h1>
            <p className="text-sm text-muted-foreground max-w-xl">
              Read our legal policies and agreements. Please review each section carefully before using our platform.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8">
          {/* Tab Navigation */}
          <div className="flex flex-col sm:flex-row gap-2 mb-8">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-5 py-3.5 rounded-xl border-2 text-left transition-all ${
                  activeTab === tab.id
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${activeTab === tab.id ? 'bg-primary/10' : 'bg-muted/30'}`}>
                  <tab.icon size={18} className={activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold uppercase tracking-wide ${activeTab === tab.id ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {tab.label}
                  </p>
                </div>
                {activeTab === tab.id && <ChevronRight size={16} className="text-primary shrink-0" />}
              </button>
            ))}
          </div>

          {/* Content Panel */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Panel Header */}
            <div className="px-6 py-5 border-b border-border bg-muted/20 flex items-center gap-3">
              {(() => {
                const tab = TABS.find(t => t.id === activeTab)!;
                return (
                  <>
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <tab.icon size={18} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-base font-extrabold uppercase tracking-tight text-foreground">{tab.label}</h2>
                      <p className="text-xs text-muted-foreground">SpeedPass Platform — Please read carefully</p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Content */}
            <div className="px-6 py-6 max-h-[600px] overflow-y-auto">
              <div className="space-y-1">
                {lines.map((line, idx) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <div key={idx} className="h-3" />;

                  // Main title (ALL CAPS, first line)
                  if (idx === 0 && trimmed === trimmed.toUpperCase() && trimmed.length > 5) {
                    return (
                      <h1 key={idx} className="text-2xl font-extrabold uppercase tracking-tight text-foreground mb-1">
                        {trimmed}
                      </h1>
                    );
                  }

                  // Section headers (e.g. "1. ACCEPTANCE OF TERMS")
                  if (/^\d+\.\s+[A-Z\s&]+$/.test(trimmed)) {
                    return (
                      <h3 key={idx} className="text-sm font-extrabold uppercase tracking-wider text-foreground mt-5 mb-2 pt-3 border-t border-border/50">
                        {trimmed}
                      </h3>
                    );
                  }

                  // Sub-headers (e.g. "Personal Information:", "Account Information:")
                  if (trimmed.endsWith(':') && !trimmed.startsWith('•') && trimmed.length < 60) {
                    return (
                      <p key={idx} className="text-sm font-bold text-foreground mt-3 mb-1">
                        {trimmed}
                      </p>
                    );
                  }

                  // Bullet points
                  if (trimmed.startsWith('•')) {
                    return (
                      <div key={idx} className="flex items-start gap-2 ml-2">
                        <span className="text-primary mt-1 shrink-0">•</span>
                        <p className="text-sm text-muted-foreground leading-relaxed">{trimmed.slice(1).trim()}</p>
                      </div>
                    );
                  }

                  // Platform name / date lines (short, italic-style)
                  if (trimmed.startsWith('SpeedPass') || trimmed.startsWith('Effective Date')) {
                    return (
                      <p key={idx} className="text-xs text-muted-foreground font-semibold mb-1">
                        {trimmed}
                      </p>
                    );
                  }

                  // Regular paragraph
                  return (
                    <p key={idx} className="text-sm text-muted-foreground leading-relaxed">
                      {trimmed}
                    </p>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border bg-muted/10 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-muted-foreground">
                Last updated: August 2026 · SpeedPass Ventures
              </p>
              <a
                href="https://speedpass.live"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary font-semibold hover:underline"
              >
                speedpass.live
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
